import { apiClient } from "@/api/client";
import Button from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/Dialog";
import Dropdown from "@/components/ui/DropDown";
import Spinner from "@/components/ui/Spinner";
import useGlobalStore from "@/store/globalStore";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import useTeamStore, { Team } from "../store/teamsStore";

interface AgencyUser {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  teams?: Array<{ id: string; name: string; team_type?: string | null }>;
}

interface TeamTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Team that will be the default source team. */
  sourceTeam: Team | null;
}

/**
 * Dialog for moving a user from one team to another within the active agency.
 * Backed by `POST /teams/transfer` (TeamTransferService) — same-agency,
 * source-membership, and atomicity guarantees live on the backend.
 */
export default function TeamTransferDialog({
  open,
  onOpenChange,
  sourceTeam,
}: TeamTransferDialogProps) {
  const { teams, transferMember, getTeams } = useTeamStore(
    useShallow((state) => ({
      teams: state.teams,
      transferMember: state.transferMember,
      getTeams: state.getTeams,
    }))
  );

  const showSuccess = useGlobalStore((s) => s.showSuccess);
  const setError = useGlobalStore((s) => s.setError);

  const [agencyUsers, setAgencyUsers] = useState<AgencyUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [destinationTeamId, setDestinationTeamId] = useState<string>("");

  // Reset selections whenever the dialog opens for a new source team.
  useEffect(() => {
    if (open) {
      setSelectedUserId("");
      setDestinationTeamId("");
    }
  }, [open, sourceTeam?.id]);

  // Fetch agency users (with their team memberships) when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingUsers(true);
      try {
        const response = await apiClient.get<{
          data: { users: AgencyUser[]; teams: Team[] };
        }>("/all-users-media-agency", { params: { with: "users" } });
        if (!cancelled) {
          setAgencyUsers(response.data?.data?.users ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load team members."
          );
        }
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, setError]);

  // Members of the source team — the only valid candidates to transfer.
  const sourceMembers = useMemo(() => {
    if (!sourceTeam) return [];
    return agencyUsers.filter((u) =>
      (u.teams ?? []).some((t) => t.id === sourceTeam.id)
    );
  }, [agencyUsers, sourceTeam]);

  // Destination teams: all other teams in the same agency.
  // (TeamTransferService also enforces same-agency on the server.)
  const destinationOptions = useMemo(() => {
    if (!sourceTeam) return [];
    return (teams ?? [])
      .filter(
        (t) =>
          t.id !== sourceTeam.id &&
          (!sourceTeam.media_agency_id ||
            t.media_agency_id === sourceTeam.media_agency_id)
      )
      .map((t) => ({ title: t.name, value: t.id }));
  }, [teams, sourceTeam]);

  const memberOptions = useMemo(
    () =>
      sourceMembers.map((u) => {
        const fullName = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
        return {
          title: fullName ? `${fullName} (${u.email})` : u.email,
          value: u.id,
        };
      }),
    [sourceMembers]
  );

  const handleSubmit = async () => {
    if (!sourceTeam) return;
    if (!selectedUserId) {
      setError("Pick a member to transfer.");
      return;
    }
    if (!destinationTeamId) {
      setError("Pick a destination team.");
      return;
    }

    setSubmitting(true);
    try {
      await transferMember(selectedUserId, sourceTeam.id, destinationTeamId);
      const dest = (teams ?? []).find((t) => t.id === destinationTeamId);
      showSuccess(
        dest?.name
          ? `Member transferred to ${dest.name}.`
          : "Member transferred."
      );
      // Refresh teams so member counts reflect the move on the next render.
      await getTeams();
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to transfer team member."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl bg-white">
        <DialogHeader>
          <div className="font-semibold text-xl text-center">
            Transfer Team Member
          </div>
          <DialogDescription className="mt-2 text-center text-gray-600">
            Move a member from{" "}
            <span className="font-semibold">{sourceTeam?.name}</span> to another
            team in the same agency.
          </DialogDescription>
          <div className="mt-5 flex flex-col gap-4">
            <div>
              <div className="font-semibold mb-1">Member</div>
              {loadingUsers ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Spinner size={16} /> Loading members...
                </div>
              ) : memberOptions.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No active members on this team.
                </div>
              ) : (
                <Dropdown
                  className="w-full"
                  selected={selectedUserId}
                  setSelected={(v) => setSelectedUserId(v.toString())}
                  options={memberOptions}
                  placeholder="Pick a member"
                />
              )}
            </div>

            <div>
              <div className="font-semibold mb-1">Destination Team</div>
              {destinationOptions.length === 0 ? (
                <div className="text-sm text-gray-500">
                  No other teams in this agency.
                </div>
              ) : (
                <Dropdown
                  className="w-full"
                  selected={destinationTeamId}
                  setSelected={(v) => setDestinationTeamId(v.toString())}
                  options={destinationOptions}
                  placeholder="Pick destination team"
                />
              )}
            </div>
          </div>
        </DialogHeader>
        <DialogFooter className="flex sm:justify-center mt-4">
          <Button
            type="primary"
            isLoading={submitting}
            onClick={handleSubmit}
            disabled={
              !selectedUserId ||
              !destinationTeamId ||
              loadingUsers ||
              memberOptions.length === 0 ||
              destinationOptions.length === 0
            }
          >
            Transfer Member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
